/*
 * Price of the selected units ("1 Deluxe Tent + 2 Dorm Beds") for the camp
 * booking tab. Same maths as the server (backend utils/stayPricing.js,
 * priceStayLines) — the server prices it again when booking.
 *
 * Guests fill the fixed-price units first, then the per-person ones; a
 * per-person unit pays for its guests (at least one per room / bed).
 * Reserve only when every unit has a reserve option.
 */

import { nightPrices, stayNights } from './stayRules';

export const unitIdOf = (unit) => unit?._id || unit?.id;
export const maxGuestsOf = (unit) => Math.max(unit?.maximumGuests || unit?.occupancy?.total || 2, 1);
export const maxRoomsOf = (unit) => {
    const inv = unit?.inventory || {};
    return Math.max((inv.activeUnits ?? inv.totalUnits ?? 1) - (inv.maintenanceUnits || 0), 1);
};

const pricingOf = (unit) => unit?.pricing?.[0] || null;
const isPerPerson = (unit) => pricingOf(unit)?.priceUnit === 'per_person';
export const nightlyOf = (unit) => Number(pricingOf(unit)?.actualPrice ?? pricingOf(unit)?.basePrice ?? 0);

// The higher price that is shown struck through (strike price, or the one the discount % implies)
export function listPriceOf(unit) {
    const p = pricingOf(unit);
    const price = nightlyOf(unit);
    const strike = Number(p?.strikePrice || 0);
    const pct = Number(p?.discountPercent || 0);
    if (strike > price) return strike;
    if (pct > 0 && pct < 100) return Math.round(price / (1 - pct / 100));
    return price;
}

// percentage: on the amount · fixed: per night, for each room (per guest for per-person units)
const taxOn = (pricing, amount, quantity) => {
    const value = Number(pricing?.tax?.value || 0);
    if (!value) return 0;
    return pricing.tax.type === 'fixed' ? value * quantity : Math.round((amount * value) / 100);
};

// reserveAmount: per guest (per_person) or per room; × nights for per_night
export const calcReserve = (pricing, { guests, rooms, nights }) => {
    let reserve = Number(pricing?.reserveAmount || 0);
    if (!reserve) return 0;
    reserve *= pricing?.priceUnit === 'per_person' ? guests : rooms;
    if (pricing?.priceUnit === 'per_night') reserve *= nights;
    return reserve;
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * lines: [{ unit, rooms }]  ·  stay: { guests, nights, checkIn? }
 * With checkIn ('YYYY-MM-DD') each night uses the unit's special prices
 * (weekend / season / early bird …, stayRules.js); nightly is then the average.
 * -> { lines: [...per unit], base, listBase, savings, tax, total, reserve, capacity }
 */
export function priceStayLines(lines, { guests, nights, checkIn }) {
    const stay = stayNights(checkIn, nights);
    const order = lines.map((_, i) => i).sort((a, b) => Number(isPerPerson(lines[a].unit)) - Number(isPerPerson(lines[b].unit)));
    const guestsOn = lines.map(() => 0);
    let left = guests;
    for (const i of order) {
        guestsOn[i] = Math.min(left, lines[i].rooms * maxGuestsOf(lines[i].unit));
        left -= guestsOn[i];
    }

    const priced = lines.map(({ unit, rooms }, i) => {
        const pricing = pricingOf(unit);
        const perPerson = isPerPerson(unit);
        const charged = perPerson ? Math.max(guestsOn[i], rooms) : guestsOn[i];
        const quantity = perPerson ? charged * nights : rooms * nights;
        const basePrice = nightlyOf(unit);
        const perNight = perPerson ? charged : rooms;
        const nightList = stay ? nightPrices(basePrice, unit?.pricingRules, stay) : [];
        const nightsTotal = nightList.length ? nightList.reduce((s, n) => s + n.price, 0) : basePrice * nights;
        const nightly = nights > 0 ? Math.round(nightsTotal / nights) : basePrice;
        // struck-through price follows the night's price (no fake saving on a dearer weekend)
        const listPrice = basePrice > 0 ? Math.round((listPriceOf(unit) * nightly) / basePrice) : 0;
        const base = nightsTotal * perNight;
        const listBase = listPrice * quantity;
        const tax =
            pricing?.tax?.type === 'fixed' && nightly !== basePrice && basePrice > 0
                ? Math.round((Number(pricing.tax.value || 0) / basePrice) * base)
                : taxOn(pricing, base, quantity);
        const special = [...new Set(nightList.map((n) => n.rule).filter(Boolean))];
        const total = base + tax;
        const reserve = Math.min(calcReserve(pricing, { guests: charged, rooms, nights }), total);
        const nightsText = plural(nights, 'night', 'nights');
        return {
            unit,
            unitId: unitIdOf(unit),
            unitName: unit?.name || 'Room',
            rooms,
            guests: guestsOn[i],
            priceUnit: pricing?.priceUnit || 'per_night',
            nightly,
            listPrice,
            base,
            listBase,
            savings: Math.max(listBase - base, 0),
            tax,
            total,
            reserve,
            // e.g. "Weekend price" — which special prices are in this stay
            specialNote: special.length ? `Includes ${special.join(', ')}` : '',
            quantityLabel: perPerson
                ? `${plural(charged, 'guest', 'guests')} × ${nightsText}`
                : `${nightsText}${rooms > 1 ? ` × ${rooms}` : ''}`
        };
    });

    const sum = (key) => priced.reduce((total, line) => total + line[key], 0);
    return {
        lines: priced,
        base: sum('base'),
        listBase: sum('listBase'),
        savings: sum('savings'),
        tax: sum('tax'),
        total: sum('total'),
        reserve: priced.length && priced.every((line) => line.reserve > 0) ? sum('reserve') : 0,
        capacity: lines.reduce((total, line) => total + line.rooms * maxGuestsOf(line.unit), 0)
    };
}

// "Deluxe Tent × 1, Dorm Bed × 2"
export const linesLabel = (lines) => lines.map((l) => `${l.unitName || l.unit?.name || 'Room'} × ${l.rooms}`).join(', ');
